import { useEffect, useMemo, useState, type MouseEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlarmClock, CheckCircle2, ChevronDown, Inbox, ListChecks, Send, UnlockKeyhole, Undo2, X } from 'lucide-react';
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  ExportCsvButton,
  InfoHint,
  Modal,
  SegmentedControl,
  Select,
  Skeleton,
  Textarea,
  toast,
} from '../../components/ui';
import { api } from '../../lib/api';
import { cn } from '../../lib/cn';
import { formatMoney } from '../../lib/format';
import {
  useActiveOrgId,
  useApprovals,
  useTimesheetAction,
  type ApprovalRow,
  type ApprovalsQueue,
  type PeriodKind,
  type TimesheetCadence,
} from './api';
import { BulkApproveDialog } from './BulkApproveDialog';
import { exportApprovalsCsv, type ApprovalsSection } from './export-csv';
import { formatHoursUnit, formatHoursValue, type HoursFormat } from './hours';
import { ReminderBar } from './ReminderBar';
import { ReviewDialog } from './ReviewDialog';
import {
  CADENCE_LABEL,
  formatSentAt,
  isDelivered,
  outcomeLabel,
  PERIOD_NOUN,
  periodsAgo,
  reminderKey,
  useReminders,
  useSendReminders,
  type ChasedCadence,
  type ReminderItem,
} from './reminders';
import { TimesheetNote, TimesheetStatusPill, formatStampShort } from './timesheet-status';

/**
 * The approver's QUEUE of timesheet periods.
 *
 * Jesse, 2026-09-29: *"Timesheets should not be approved one week at a time…
 * we most care about daily or monthly submissions… most important for us is
 * contractors tracking their hours and committing to them."* A timesheet is a
 * PERIOD per person — a day, a week or a month, per their frequency — so this
 * is no longer a week roster with a week picker. It is three lists, in the
 * order an approver acts on them:
 *
 * 1. **Waiting for review** — submitted periods, oldest first. Approve is one
 *    click (it is undoable: the row moves to Recently decided with Reopen);
 *    Send back asks for a note, because a rejection without a reason is a
 *    period bounced back to somebody guessing.
 * 2. **Not submitted** — closed periods of people a submission is expected
 *    from, grouped by person, each with Remind (the same send the scheduled run
 *    makes). A period the server would refuse to chase (past the window) says
 *    so instead of offering a button.
 * 3. **Recently decided** — collapsed; where a decision is taken back.
 *
 * Under them, who submits and how often — the one place the frequency is
 * visible beside the work it produces.
 */

type Filter = 'all' | 'waiting' | 'missing' | 'decided';
const FILTERS: Filter[] = ['all', 'waiting', 'missing', 'decided'];

/** The server's defaults, used only until the reminder overview arrives. */
const DEFAULT_CHASE_WINDOW: Record<PeriodKind, number> = { DAY: 10, WEEK: 4, MONTH: 2 };

const KIND_LABEL: Record<PeriodKind, string> = { DAY: 'Day', WEEK: 'Week', MONTH: 'Month' };

/** Open periods shown per person before "Show N earlier" — a daily submitter can be ten behind. */
const MISSING_SHOWN = 3;

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * Open this person's timesheet on the period in question. Always names the
 * person — your own row too — so a new tab opens THAT sheet rather than
 * whichever person this browser last picked.
 */
function sheetHref(row: ApprovalRow): string {
  return `/time?view=week&date=${row.periodStart}&person=${row.person.id}`;
}

/** A plain left click, as opposed to one asking for a new tab or window. */
function isPlainClick(event: MouseEvent<HTMLAnchorElement>): boolean {
  return (
    event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
  );
}

/** A small uppercase tag beside a person's name ("You", "Archived"). */
function tagChip(label: string): ReactNode {
  return (
    <span className="ml-1.5 rounded bg-grey-100 px-1 py-px text-[10px] font-bold uppercase tracking-wide text-grey-500">
      {label}
    </span>
  );
}

/** Stable key for one person's period — the unit every list here is about. */
function periodKey(row: ApprovalRow): string {
  return `${row.person.id}|${row.periodKind}|${row.periodStart}`;
}

export interface ApprovalsViewProps {
  hoursFormat: HoursFormat;
  /**
   * Move the page to a person's period IN PLACE (TimePage state). A router
   * navigation to `/time?…` from inside /time is rewritten back by the page's
   * URL sync, so every link here intercepts a plain click and calls this.
   * `personId` null = the viewer's own sheet.
   */
  onOpenSheet: (personId: string | null, date: string) => void;
  /**
   * Can this person's sheet be opened on /time at all? Only people the picker
   * lists can be (active people). An archived person's submitted period still
   * waits here to be decided, but a link to their sheet would open somebody
   * else's — so their name is printed, not linked, and says why.
   */
  canOpenSheet: (personId: string) => boolean;
}

export function ApprovalsView({ hoursFormat, onOpenSheet, canOpenSheet }: ApprovalsViewProps) {
  const queue = useApprovals(true);
  const reminders = useReminders(true);
  const send = useSendReminders();
  const approve = useTimesheetAction('approve');
  const orgId = useActiveOrgId();
  const queryClient = useQueryClient();

  /** Filters live in the URL, like every list filter in the app. */
  const [searchParams, setSearchParams] = useSearchParams();
  const param = searchParams.get('status');
  const filter: Filter = FILTERS.includes(param as Filter) ? (param as Filter) : 'all';
  const setFilter = (next: Filter) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === 'all') params.delete('status');
        else params.set('status', next);
        return params;
      },
      { replace: true },
    );

  /** Ticked timesheet ids. Rows are re-read from the live queue every render. */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [dialog, setDialog] = useState<{ action: 'reject' | 'reopen'; row: ApprovalRow } | null>(
    null,
  );
  const [showRecent, setShowRecent] = useState(false);
  /** People whose older open periods are expanded — the latest few show by default. */
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [approvingId, setApprovingId] = useState<string | null>(null);
  /** The submitted period open in Review — its own entries and nothing else. */
  const [reviewing, setReviewing] = useState<ApprovalRow | null>(null);
  const [remindingKey, setRemindingKey] = useState<string | null>(null);
  useEffect(() => setSelected(new Set()), [filter]);

  /** Who tracks time and how often they submit — `/team/:id/time-policy`. */
  const policy = useMutation({
    mutationFn: (input: {
      id: string;
      tracksTime: boolean;
      timesheetCadence?: TimesheetCadence;
      timesheetRequired?: boolean;
    }) =>
      api.post(`/team/${input.id}/time-policy`, {
        tracksTime: input.tracksTime,
        ...(input.timesheetCadence ? { timesheetCadence: input.timesheetCadence } : {}),
        ...(input.timesheetRequired !== undefined ? { timesheetRequired: input.timesheetRequired } : {}),
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['time', orgId] });
      void queryClient.invalidateQueries({ queryKey: ['team'] });
    },
  });

  const data = queue.data;

  /** Missing periods grouped by person, in the order the queue sent them. */
  const missingByPerson = useMemo(() => {
    const groups = new Map<string, ApprovalRow[]>();
    for (const row of data?.missing ?? []) {
      const list = groups.get(row.person.id) ?? [];
      list.push(row);
      groups.set(row.person.id, list);
    }
    return [...groups.values()]
      .map((rows) => [...rows].sort((a, b) => (a.periodStart < b.periodStart ? 1 : -1)))
      .sort((a, b) => a[0].person.name.localeCompare(b[0].person.name));
  }, [data]);

  if (queue.isPending) {
    return (
      <Card aria-busy>
        <div className="space-y-3 p-4">
          <Skeleton className="h-14 w-full" />
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </Card>
    );
  }

  if (queue.isError || !data) {
    return (
      <ErrorState
        card
        title="Couldn't load the approvals queue"
        message={
          queue.error instanceof Error ? queue.error.message : 'Something went wrong loading it.'
        }
        onRetry={() => queue.refetch()}
      />
    );
  }

  const h = (n: number) => formatHoursUnit(n, hoursFormat);
  const today = data.today;
  const chaseWindow = reminders.data?.chaseWindow ?? DEFAULT_CHASE_WINDOW;
  const zone = reminders.data?.settings.timeZone ?? 'America/Chicago';

  const waitingForMe = data.awaiting.filter((r) => r.can.approve && r.id);
  const batch = waitingForMe.filter((r) => selected.has(r.id!));
  const allTicked = waitingForMe.length > 0 && batch.length === waitingForMe.length;

  const chaseable = (row: ApprovalRow) =>
    periodsAgo(row.periodStart, row.periodKind, today) <= chaseWindow[row.periodKind];
  const itemOf = (row: ApprovalRow): ReminderItem => ({
    personId: row.person.id,
    periodKind: row.periodKind,
    periodStart: row.periodStart,
  });
  /**
   * Has anything about THIS period actually gone out? Every missing row carries
   * its own last reminder (scheduler or human), read from the same log the
   * scheduler writes — so the period the 9:00 run chased says so, instead of
   * reading "not reminded" and inviting a second email.
   */
  const alreadyReminded = (row: ApprovalRow) => isDelivered(row.remindedOutcome ?? null);
  /**
   * The bulk chase: per person, their most recent open period that may still
   * be chased and has not been — ONE message per person. Never the viewer's
   * own; you never email yourself. Every missing row has already ENDED (the
   * server lists nothing still being worked), so nothing here is premature.
   */
  const bulkItems: ReminderItem[] = missingByPerson.flatMap((rows) => {
    const next = rows.find(
      (r) => !r.isOwn && r.person.email && chaseable(r) && !alreadyReminded(r),
    );
    return next ? [itemOf(next)] : [];
  });

  const remind = async (items: ReminderItem[], who: string) => {
    try {
      const result = await send.mutateAsync({ items });
      const delivered = result.sent.filter((s) => isDelivered(s.outcome));
      const failed = result.sent.filter((s) => !isDelivered(s.outcome));
      const filed = result.emailMode === 'live' ? '' : ' — written to .mail, not transmitted';
      if (delivered.length === 0 && failed.length > 0) {
        toast.error(`Nothing went out — ${failed[0].name}: ${outcomeLabel(failed[0].outcome)}.`);
      } else if (delivered.length === 0) {
        toast.error(`Nothing went out — ${who} has nothing open to chase.`);
      } else {
        // One message names its period: the bulk chase picks each person's
        // latest period NOT yet reminded, which is not always the newest row.
        const only = delivered.length === 1 ? delivered[0] : null;
        const about = only
          ? data.missing.find((r) => reminderKey(itemOf(r)) === reminderKey(only))?.label
          : undefined;
        toast.success(
          `Reminded ${only ? `${only.name}${about ? ` about ${about}` : ''}` : plural(delivered.length, 'person', 'people')}${filed}${
            failed.length > 0 ? ` · ${failed.length} could not be reached` : ''
          }.`,
        );
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'The reminder was refused.');
    }
  };

  const approveOne = async (row: ApprovalRow) => {
    if (!row.id) return;
    setApprovingId(row.id);
    try {
      await approve.mutateAsync({ timesheetId: row.id });
      toast.success(`${row.person.name} · ${row.label} approved.`);
      setReviewing((open) => (open?.id === row.id ? null : open));
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(row.id!);
        return next;
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'That approval was refused.');
    } finally {
      setApprovingId(null);
    }
  };

  const setPolicy = (person: ApprovalsQueue['people'][number], value: string) => {
    const tracksTime = value !== 'OFF';
    policy.mutate(
      {
        id: person.id,
        tracksTime,
        timesheetCadence: tracksTime ? (value as TimesheetCadence) : undefined,
      },
      {
        onSuccess: () =>
          toast.success(
            !tracksTime
              ? `${person.name} no longer tracks time.`
              : value === 'NEVER'
                ? `${person.name} tracks time but is not asked for a timesheet.`
                : `${person.name} submits ${CADENCE_LABEL[value as TimesheetCadence].toLowerCase()}.`,
          ),
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : `Could not change ${person.name}.`),
      },
    );
  };

  const setRequired = (person: ApprovalsQueue['people'][number], required: boolean) => {
    policy.mutate(
      { id: person.id, tracksTime: true, timesheetRequired: required },
      {
        onSuccess: () =>
          toast.success(
            required
              ? `${person.name} must submit — a period not sent in is late.`
              : `${person.name} may submit, and is never chased.`,
          ),
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : `Could not change ${person.name}.`),
      },
    );
  };

  const showWaiting = filter === 'all' || filter === 'waiting';
  const showMissing = filter === 'all' || filter === 'missing';
  const showDecided = filter === 'all' || filter === 'decided';
  const recentOpen = filter === 'decided' || showRecent;

  /** A person's open periods as rendered: the latest few, or all once expanded. */
  const visibleMissing = (rows: ApprovalRow[]) =>
    expanded.has(rows[0].person.id) ? rows : rows.slice(0, MISSING_SHOWN);

  /**
   * Exactly the timesheets on screen, in the screen's order — never a collapsed
   * section, never the periods behind "Show N earlier" — and each PERIOD once.
   * A period sent back sits in Not submitted AND in Recently decided; the file
   * keeps its first (Not submitted) row, which carries the same status,
   * reviewer and note, so the footing counts its hours once and matches the
   * screen.
   */
  const exportRows: Array<{ section: ApprovalsSection; row: ApprovalRow }> = (() => {
    const rendered: Array<{ section: ApprovalsSection; row: ApprovalRow }> = [
      ...(showWaiting
        ? data.awaiting.map((row) => ({ section: 'Waiting for review' as const, row }))
        : []),
      ...(showMissing
        ? missingByPerson.flatMap(visibleMissing).map((row) => ({ section: 'Not submitted' as const, row }))
        : []),
      ...(showDecided && recentOpen
        ? data.recent.map((row) => ({ section: 'Recently decided' as const, row }))
        : []),
    ];
    const seen = new Set<string>();
    return rendered.filter(({ row }) => {
      const key = periodKey(row);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  })();

  /** Your own row always opens (null = the viewer's sheet); anyone else only if listed. */
  const openable = (row: ApprovalRow) => row.isOwn || canOpenSheet(row.person.id);

  /**
   * The person's name (or "Open") as a real link — tab-reachable, and
   * cmd/middle-click opens the sheet in a new tab — whose plain click moves
   * this page in place. For a person whose sheet cannot be opened (archived)
   * it is plain text with an "Archived" tag: a link that opened the wrong
   * person's week — the viewer's own — is worse than no link.
   */
  const sheetLink = (row: ApprovalRow, children: ReactNode, className: string) =>
    openable(row) ? (
      <Link
        to={sheetHref(row)}
        className={className}
        onClick={(event) => {
          if (!isPlainClick(event)) return;
          event.preventDefault();
          onOpenSheet(row.isOwn ? null : row.person.id, row.periodStart);
        }}
      >
        {children}
      </Link>
    ) : (
      <>
        <span className={cn(className, 'hover:no-underline')}>{children}</span>
        {tagChip('Archived')}
      </>
    );
  const hasArchivedRows = [...data.awaiting, ...data.recent].some((row) => !openable(row));

  const cadencesInUse = [
    ...new Set(
      data.people
        .filter((p) => p.tracksTime && p.cadence !== 'NEVER' && p.required)
        .map((p) => p.cadence as ChasedCadence),
    ),
  ];

  const tickbox = (checked: boolean, onChange: () => void, label: string) => (
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      aria-label={label}
      className="h-4 w-4 cursor-pointer rounded border-grey-300 text-accent focus:ring-accent/30"
    />
  );

  const youChip = tagChip('You');

  /**
   * Hours + what they cost us, as short labels — never a paragraph. Cost, not
   * billable value (Jesse, 2026-10-05): approving a contractor's month is
   * approving what we pay for it.
   */
  const figures = (row: ApprovalRow) => (
    <div className="text-right">
      <p className="num font-bold text-secondary">{h(row.hours)}</p>
      {row.costBasis === 'salary' ? (
        <p className="text-caption text-grey-500">salaried</p>
      ) : row.costBasis === 'no-rate' ? (
        <p className="text-caption text-amber-700">no cost rate</p>
      ) : row.cost !== null && row.cost > 0 ? (
        <p className="text-caption text-grey-500">
          <span className="num">{formatMoney(row.cost)}</span> cost
        </p>
      ) : null}
      {row.costBasis === 'hourly' && row.uncostedHours > 0 && (
        <p className="text-caption text-amber-700">
          + <span className="num">{formatHoursValue(row.uncostedHours, hoursFormat)}</span> no cost rate
        </p>
      )}
    </div>
  );

  const periodCaption = (row: ApprovalRow, extra?: ReactNode) => (
    <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-caption text-grey-500">
      <span className="rounded bg-grey-100 px-1.5 py-px text-[10px] font-bold uppercase tracking-wide text-grey-600">
        {KIND_LABEL[row.periodKind]}
      </span>
      <span className="font-semibold text-grey-700">{row.label}</span>
      {extra}
    </p>
  );

  const summary = data.summary;
  const awaitingOthers = data.awaiting.length - waitingForMe.length;

  return (
    <>
      {/* ── Header: the verdict, the filter, the export ─────────────────── */}
      <Card className="mb-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
          <div className="min-w-0 flex-1 basis-full text-body-sm text-grey-600 sm:basis-0">
            {data.awaiting.length > 0 ? (
              <>
                <span className="font-bold text-secondary">
                  {plural(data.awaiting.length, 'timesheet')} waiting for review
                </span>{' '}
                · <span className="num">{h(summary.awaitingHours)}</span>
              </>
            ) : (
              <span className="font-bold text-secondary">Nothing waiting for review</span>
            )}
            {data.missing.length > 0 && (
              <>
                {' · '}
                {plural(missingByPerson.length, 'person', 'people')} behind
              </>
            )}
            <InfoHint label="How approvals work" className="ml-1" width={320}>
              <p>
                Each person submits a timesheet for their own period — a day, a week or a month,
                set by their frequency below. Screens still show a week; what is submitted,
                locked and approved is the period.
              </p>
              <p className="mt-2">
                Approving locks the period and does not bill anything — invoicing is separate. A
                decision can be taken back from Recently decided.
              </p>
              <p className="mt-2">
                {summary.expectedPeople} {summary.expectedPeople === 1 ? 'person is' : 'people are'}{' '}
                expected to submit; {summary.notExpected} {summary.notExpected === 1 ? 'is' : 'are'}{' '}
                not, and are never chased.
              </p>
              {hasArchivedRows && (
                <p className="mt-2">
                  <strong>Archived</strong> — a period submitted before its owner was archived still
                  waits here to be decided, but their timesheet no longer opens on this screen.
                </p>
              )}
            </InfoHint>
          </div>
          <ExportCsvButton
            count={exportRows.length}
            noun="timesheets"
            scope="on screen"
            onClick={() => exportApprovalsCsv(exportRows, filter === 'all' ? '' : filter, today)}
          />
        </div>
        <div className="border-t border-grey-100 px-4 py-2.5">
          <SegmentedControl<Filter>
            value={filter}
            onChange={setFilter}
            ariaLabel="Show"
            size="md"
            scrollable
            options={[
              { value: 'all', label: 'All' },
              { value: 'waiting', label: 'Waiting', count: data.awaiting.length },
              { value: 'missing', label: 'Not submitted', count: data.missing.length },
              { value: 'decided', label: 'Decided', count: data.recent.length },
            ]}
          />
        </div>
      </Card>

      {/* ── 1 · Waiting for review ─────────────────────────────────────── */}
      {showWaiting && (
        <Card className="mb-3">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-grey-100 px-4 py-3">
            {waitingForMe.length > 1 &&
              tickbox(
                allTicked,
                () =>
                  setSelected(allTicked ? new Set() : new Set(waitingForMe.map((r) => r.id!))),
                `Select all ${waitingForMe.length} timesheets you can approve`,
              )}
            <h3 className="min-w-0 flex-1 text-heading-4 text-secondary">
              Waiting for review
              <span className="ml-2 text-body-sm font-normal text-grey-500">
                {data.awaiting.length === 0
                  ? 'nothing'
                  : awaitingOthers > 0
                    ? `${waitingForMe.length} for you · ${awaitingOthers} for an admin`
                    : 'oldest first'}
              </span>
            </h3>
            {batch.length > 0 && (
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
                  <X className="h-3.5 w-3.5" aria-hidden />
                  Clear
                </Button>
                <Button size="sm" onClick={() => setBulkOpen(true)}>
                  <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                  Approve {batch.length}
                </Button>
              </div>
            )}
          </div>
          {data.awaiting.length === 0 ? (
            <EmptyState
              className="py-10"
              icon={<CheckCircle2 aria-hidden />}
              title="Nothing is waiting for you"
              description="Submitted timesheets land here, oldest first."
              action={
                data.missing.length > 0 && filter === 'waiting' ? (
                  <Button variant="outline" size="sm" onClick={() => setFilter('missing')}>
                    See who has not submitted
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <ul className="divide-y divide-grey-100">
              {data.awaiting.map((row) => {
                const mine = row.can.approve && row.id;
                return (
                  <li key={row.id ?? `${row.person.id}-${row.periodStart}`} className="px-4 py-3">
                    <div className="grid grid-cols-[1.25rem_minmax(0,1fr)_auto] items-start gap-x-3 gap-y-2 md:grid-cols-[1.25rem_minmax(0,1fr)_9rem_auto] md:items-center">
                      <div className="pt-0.5 md:pt-0">
                        {mine &&
                          tickbox(
                            selected.has(row.id!),
                            () =>
                              setSelected((prev) => {
                                const next = new Set(prev);
                                if (next.has(row.id!)) next.delete(row.id!);
                                else next.add(row.id!);
                                return next;
                              }),
                            `Select ${row.person.name}, ${row.label}`,
                          )}
                      </div>
                      <div className="min-w-0">
                        {sheetLink(
                          row,
                          row.person.name,
                          'font-bold text-secondary no-underline hover:underline',
                        )}
                        {row.isOwn && youChip}
                        {periodCaption(
                          row,
                          <>
                            {row.submittedAt && (
                              <span>Submitted {formatStampShort(row.submittedAt)}</span>
                            )}
                            {!row.expected && <span className="text-grey-400">off schedule</span>}
                          </>,
                        )}
                      </div>
                      {figures(row)}
                      <div className="col-span-3 flex flex-wrap items-center justify-end gap-2 md:col-span-1">
                        {row.id && (
                          <Button variant="outline" size="sm" onClick={() => setReviewing(row)}>
                            <ListChecks className="h-3.5 w-3.5" aria-hidden />
                            Review
                          </Button>
                        )}
                        {mine ? (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDialog({ action: 'reject', row })}
                            >
                              <Undo2 className="h-3.5 w-3.5" aria-hidden />
                              Send back
                            </Button>
                            <Button
                              size="sm"
                              loading={approvingId === row.id}
                              disabled={approvingId !== null && approvingId !== row.id}
                              onClick={() => approveOne(row)}
                            >
                              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                              Approve
                            </Button>
                          </>
                        ) : (
                          <span className="text-caption text-grey-500">
                            {row.isOwn ? 'Your own — an admin approves it' : 'For an admin'}
                          </span>
                        )}
                      </div>
                      {row.note && (
                        <TimesheetNote
                          note={row.note}
                          author={row.person.name}
                          className="col-span-3 md:col-span-2 md:col-start-2"
                        />
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      )}

      {/* ── 2 · Not submitted ──────────────────────────────────────────── */}
      {showMissing && (
        <Card className="mb-3">
          <div className="border-b border-grey-100 px-4 py-3">
            <h3 className="text-heading-4 text-secondary">
              Not submitted
              <span className="ml-2 text-body-sm font-normal text-grey-500">
                {data.missing.length === 0
                  ? 'everyone is in'
                  : `${plural(data.missing.length, 'period')} · ${summary.missingNoTime} with nothing logged`}
              </span>
            </h3>
          </div>
          <ReminderBar
            canApprove
            cadences={cadencesInUse}
            action={
              <Button
                variant="outline"
                size="sm"
                className="min-w-0 flex-1 justify-center sm:flex-none"
                disabled={bulkItems.length === 0 || send.isPending}
                loading={send.isPending && remindingKey === 'all'}
                onClick={async () => {
                  setRemindingKey('all');
                  await remind(bulkItems, 'nobody');
                  setRemindingKey(null);
                }}
              >
                <Send className="h-3.5 w-3.5" aria-hidden />
                {bulkItems.length === 0 ? 'Nobody to remind' : `Remind ${bulkItems.length}`}
              </Button>
            }
          />
          {missingByPerson.length === 0 ? (
            <EmptyState
              className="py-10"
              icon={<Inbox aria-hidden />}
              title="Everyone expected is in"
              description="Every closed period of everyone with a frequency has been submitted."
            />
          ) : (
            <ul className="divide-y divide-grey-100">
              {missingByPerson.map((rows) => {
                const person = rows[0].person;
                const own = rows[0].isOwn;
                const open = expanded.has(person.id);
                const shown = visibleMissing(rows);
                const hidden = rows.length - shown.length;
                return (
                  <li key={person.id} className="px-4 py-3">
                    <p className="flex flex-wrap items-baseline gap-x-2">
                      <span className="font-bold text-secondary">{person.name}</span>
                      {own && youChip}
                      <span className="text-caption text-grey-500">
                        {CADENCE_LABEL[person.cadence]} ·{' '}
                        {person.type === 'CONTRACTOR' ? 'Contractor' : 'Staff'} ·{' '}
                        {plural(rows.length, PERIOD_NOUN[rows[0].periodKind])} open
                      </span>
                    </p>
                    <ul className="mt-1.5 space-y-1.5">
                      {shown.map((row) => {
                        const key = reminderKey(itemOf(row));
                        // This period's OWN last reminder — not whichever one
                        // the next scheduled run happens to target.
                        const outcome = row.remindedOutcome ?? null;
                        const sentAt = isDelivered(outcome)
                          ? formatSentAt(row.remindedAt ?? null, zone)
                          : null;
                        const tooOld = !chaseable(row);
                        return (
                          <li
                            key={key}
                            className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 rounded-lg bg-grey-50 px-3 py-2 md:grid-cols-[minmax(0,1fr)_7rem_11rem_6.5rem]"
                          >
                            <div className="min-w-0">
                              {periodCaption(
                                row,
                                row.status === 'REJECTED' ? (
                                  <span className="rounded bg-status-late/15 px-1.5 py-px text-[10px] font-bold uppercase tracking-wide text-status-late">
                                    Sent back
                                  </span>
                                ) : undefined,
                              )}
                            </div>
                            <p className="text-right text-body-sm">
                              {row.hours > 0 ? (
                                <span className="num font-semibold text-secondary">{h(row.hours)}</span>
                              ) : (
                                <span className="text-status-partial">nothing logged</span>
                              )}
                            </p>
                            <p className="text-caption text-grey-500 md:text-right">
                              {own ? null : sentAt ? (
                                <span className="inline-flex items-center gap-1">
                                  <AlarmClock className="h-3 w-3 text-status-paid" aria-hidden />
                                  Reminded {sentAt}
                                </span>
                              ) : tooOld ? (
                                `older than ${chaseWindow[row.periodKind]} ${PERIOD_NOUN[row.periodKind]}s`
                              ) : outcome ? (
                                outcomeLabel(outcome)
                              ) : (
                                'not reminded'
                              )}
                            </p>
                            <div className="flex justify-end">
                              {own ? (
                                sheetLink(
                                  row,
                                  'Open',
                                  'text-body-sm font-semibold text-accent no-underline hover:underline',
                                )
                              ) : tooOld ? (
                                <span className="text-caption text-grey-400">Talk to them</span>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={!person.email || send.isPending}
                                  loading={send.isPending && remindingKey === key}
                                  onClick={async () => {
                                    setRemindingKey(key);
                                    await remind([itemOf(row)], person.name);
                                    setRemindingKey(null);
                                  }}
                                >
                                  <Send className="h-3.5 w-3.5" aria-hidden />
                                  {sentAt ? 'Again' : 'Remind'}
                                </Button>
                              )}
                            </div>
                            {row.reviewNote && (
                              <TimesheetNote
                                note={row.reviewNote}
                                author={row.reviewedBy?.name ?? 'Approver'}
                                tone="warning"
                                className="col-span-2 md:col-span-4"
                              />
                            )}
                          </li>
                        );
                      })}
                    </ul>
                    {(hidden > 0 || (open && rows.length > MISSING_SHOWN)) && (
                      <button
                        type="button"
                        className="mt-1.5 text-caption font-semibold text-accent hover:underline"
                        aria-expanded={open}
                        onClick={() =>
                          setExpanded((prev) => {
                            const next = new Set(prev);
                            if (next.has(person.id)) next.delete(person.id);
                            else next.add(person.id);
                            return next;
                          })
                        }
                      >
                        {open
                          ? 'Show fewer'
                          : `Show ${plural(hidden, `earlier ${PERIOD_NOUN[rows[0].periodKind]}`)}`}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      )}

      {/* ── 3 · Recently decided (secondary) ───────────────────────────── */}
      {showDecided && (
        <Card className="mb-3">
          <button
            type="button"
            className="flex w-full items-center gap-2 px-4 py-3 text-left"
            aria-expanded={recentOpen}
            onClick={() => (filter === 'decided' ? undefined : setShowRecent((v) => !v))}
          >
            <span className="min-w-0 flex-1 text-heading-4 text-secondary">
              Recently decided
              <span className="ml-2 text-body-sm font-normal text-grey-500">
                {data.recent.length === 0 ? 'none in 45 days' : `${data.recent.length} · last 45 days`}
              </span>
            </span>
            {filter !== 'decided' && (
              <ChevronDown
                className={cn('h-4 w-4 text-grey-400 transition-transform', recentOpen && 'rotate-180')}
                aria-hidden
              />
            )}
          </button>
          {recentOpen && data.recent.length > 0 && (
            <ul className="divide-y divide-grey-100 border-t border-grey-100">
              {data.recent.map((row) => (
                <li key={row.id ?? `${row.person.id}-${row.periodStart}`} className="px-4 py-3">
                  <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-3 gap-y-2 md:grid-cols-[minmax(0,1fr)_9rem_auto] md:items-center">
                    <div className="min-w-0">
                      {sheetLink(
                        row,
                        row.person.name,
                        'font-bold text-secondary no-underline hover:underline',
                      )}
                      {row.isOwn && youChip}
                      {periodCaption(
                        row,
                        <>
                          <TimesheetStatusPill status={row.status} />
                          <span>
                            by {row.reviewedBy?.name ?? 'an approver'}
                            {row.reviewedAt ? ` · ${formatStampShort(row.reviewedAt)}` : ''}
                          </span>
                        </>,
                      )}
                    </div>
                    {figures(row)}
                    <div className="col-span-2 flex justify-end md:col-span-1">
                      {row.can.reopen && (
                        <Button variant="ghost" size="sm" onClick={() => setDialog({ action: 'reopen', row })}>
                          <UnlockKeyhole className="h-3.5 w-3.5" aria-hidden />
                          Reopen
                        </Button>
                      )}
                    </div>
                    {row.reviewNote && (
                      <TimesheetNote
                        note={row.reviewNote}
                        author={row.reviewedBy?.name ?? 'Approver'}
                        tone={row.status === 'REJECTED' ? 'warning' : 'neutral'}
                        className="col-span-2 md:col-span-3"
                      />
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {/* ── Who submits, and how often ─────────────────────────────────── */}
      {filter === 'all' && (
        <Card>
          <div className="border-b border-grey-100 px-4 py-3">
            <div className="text-heading-4 text-secondary" role="heading" aria-level={3}>
              Who submits
              <span className="ml-2 text-body-sm font-normal text-grey-500">
                {summary.expectedPeople} expected · {summary.notExpected} not
              </span>
              <InfoHint label="Submission frequency" className="ml-1" width={320}>
                <p>
                  Each person submits a timesheet for a day, a week or a month. Contractors start
                  on monthly and staff on weekly when they begin tracking time.
                </p>
                <p className="mt-2">
                  <strong>Required</strong> — a period they have not sent in is late: listed under
                  Not submitted and reminded. Untick it and submitting is optional — they can still
                  send a period in, and nothing is chased. Contractors start optional.
                </p>
                <p className="mt-2">
                  <strong>No timesheet</strong> — tracks time, is never asked to submit.{' '}
                  <strong>Not tracking</strong> — no timer, no timesheet. Tracking needs a Solid
                  account, linked on /team.
                </p>
                <p className="mt-2">
                  <strong>No account · no timer</strong> — marked as tracking from before accounts
                  were required. Still asked for timesheets, but there is no timer until an account
                  is linked on /team, and until then the only change is to stop tracking.
                </p>
              </InfoHint>
            </div>
          </div>
          <ul className="divide-y divide-grey-100">
            {[...data.people]
              .sort(
                (a, b) =>
                  Number(b.expected) - Number(a.expected) || a.name.localeCompare(b.name),
              )
              .map((person) => {
                const value = person.tracksTime ? person.cadence : 'OFF';
                return (
                  <li
                    key={person.id}
                    className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`/team/${person.id}`}
                        className={cn(
                          'font-semibold no-underline hover:underline',
                          person.expected ? 'text-secondary' : 'text-grey-500',
                        )}
                      >
                        {person.name}
                      </Link>
                      <span className="ml-2 text-caption text-grey-500">
                        {person.type === 'CONTRACTOR' ? 'Contractor' : 'Staff'}
                      </span>
                      {/* Tracking with no Solid account: a Harvest-era row the
                          cadence migration left tracking. They ARE chased (the
                          audience is tracking + a frequency), and they get no
                          timer — both said here, in amber, never "not
                          tracking". Its own line on a phone, beside the name
                          on a desktop. */}
                      {!person.hasAccount && person.tracksTime && (
                        <span className="block text-caption font-semibold text-status-partial sm:ml-2 sm:inline">
                          No account · no timer
                        </span>
                      )}
                    </div>
                    {person.hasAccount &&
                      data.viewer.isAdmin &&
                      person.tracksTime &&
                      person.cadence !== 'NEVER' && (
                        <label className="flex cursor-pointer items-center gap-1.5 text-caption text-grey-600">
                          <input
                            type="checkbox"
                            className="h-4 w-4 cursor-pointer rounded border-grey-300 text-accent focus:ring-accent/30"
                            checked={person.required}
                            disabled={policy.isPending}
                            onChange={(e) => setRequired(person, e.target.checked)}
                            aria-label={`Submitting is required of ${person.name}`}
                          />
                          Required
                        </label>
                      )}
                    {!person.required && person.tracksTime && person.cadence !== 'NEVER' && !data.viewer.isAdmin && (
                      <span className="text-caption text-grey-500">optional</span>
                    )}
                    {person.hasAccount && data.viewer.isAdmin ? (
                      <Select
                        className="w-40"
                        selectSize="sm"
                        aria-label={`How often ${person.name} submits`}
                        value={value}
                        disabled={policy.isPending}
                        onChange={(e) => setPolicy(person, e.target.value)}
                        options={[
                          { value: 'DAILY', label: 'Daily' },
                          { value: 'WEEKLY', label: 'Weekly' },
                          { value: 'MONTHLY', label: 'Monthly' },
                          { value: 'NEVER', label: 'No timesheet' },
                          { value: 'OFF', label: 'Not tracking' },
                        ]}
                      />
                    ) : person.tracksTime && data.viewer.isAdmin ? (
                      // The server refuses a frequency change without an
                      // account (409) but accepts switching tracking off — so
                      // that is the one change offered.
                      <Select
                        className="w-40"
                        selectSize="sm"
                        aria-label={`How often ${person.name} submits`}
                        value={person.cadence}
                        disabled={policy.isPending}
                        onChange={(e) => setPolicy(person, e.target.value)}
                        options={[
                          { value: person.cadence, label: CADENCE_LABEL[person.cadence] },
                          { value: 'OFF', label: 'Not tracking' },
                        ]}
                      />
                    ) : (
                      <span className="w-40 text-right text-caption text-grey-500">
                        {person.tracksTime
                          ? CADENCE_LABEL[person.cadence]
                          : !person.hasAccount
                            ? 'No account · not tracking'
                            : 'Not tracking'}
                      </span>
                    )}
                  </li>
                );
              })}
          </ul>
        </Card>
      )}

      <BulkApproveDialog
        open={bulkOpen}
        rows={batch}
        hoursFormat={hoursFormat}
        onClose={() => setBulkOpen(false)}
        onDone={() => setSelected(new Set())}
      />
      <DecisionDialog dialog={dialog} hoursFormat={hoursFormat} onClose={() => setDialog(null)} />
      <ReviewDialog
        row={reviewing}
        hoursFormat={hoursFormat}
        canEdit={data.viewer.isAdmin}
        approving={reviewing !== null && approvingId === reviewing.id}
        onClose={() => setReviewing(null)}
        onApprove={(r) => void approveOne(r)}
        onSendBack={(r) => {
          setReviewing(null);
          setDialog({ action: 'reject', row: r });
        }}
      />
    </>
  );
}

/**
 * Send back (a note is required — it is the only thing telling them what to
 * fix) or Reopen an approved period (note optional). Addressed by timesheet id.
 */
function DecisionDialog({
  dialog,
  hoursFormat,
  onClose,
}: {
  dialog: { action: 'reject' | 'reopen'; row: ApprovalRow } | null;
  hoursFormat: HoursFormat;
  onClose: () => void;
}) {
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [noteError, setNoteError] = useState<string | null>(null);
  const mutation = useTimesheetAction(dialog?.action ?? 'reject');

  useEffect(() => {
    setNote('');
    setError(null);
    setNoteError(null);
  }, [dialog]);

  if (!dialog) return null;
  const { action, row } = dialog;
  const reject = action === 'reject';

  const run = async () => {
    const trimmed = note.trim();
    if (reject && !trimmed) {
      setNoteError('Add a note — it is the only thing telling them what to fix.');
      return;
    }
    try {
      await mutation.mutateAsync(
        row.id
          ? { timesheetId: row.id, note: trimmed || null }
          : {
              personId: row.person.id,
              periodKind: row.periodKind,
              periodStart: row.periodStart,
              note: trimmed || null,
            },
      );
      toast.success(
        reject
          ? `Sent back to ${row.person.name} with your note.`
          : `${row.person.name} · ${row.label} reopened for editing.`,
      );
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not work — try again.');
    }
  };

  return (
    <Modal
      open
      onOpenChange={(next) => !next && onClose()}
      title={reject ? 'Send this timesheet back' : 'Reopen this approved timesheet'}
      description={
        <>
          {row.person.name} · {row.label} ·{' '}
          <span className="num font-semibold text-secondary">
            {formatHoursUnit(row.hours, hoursFormat)}
          </span>
        </>
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={run}
            loading={mutation.isPending}
            variant={reject ? 'destructive' : 'primary'}
          >
            {reject ? 'Send back' : 'Reopen'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-body-sm text-grey-500">
          {reject
            ? 'It reopens for editing and this note is the first thing its owner sees.'
            : 'The approval is withdrawn and the period becomes editable again. Your name and this note are shown to its owner.'}
        </p>
        <Textarea
          label={reject ? 'What needs to change?' : 'Why is it being reopened? (optional)'}
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
            setNoteError(null);
          }}
          error={noteError ?? undefined}
          rows={3}
          placeholder={
            reject
              ? 'Tuesday has 11 h on Meridian with no description — please split and describe it.'
              : 'Missing Friday — please add it and resubmit'
          }
          textareaClassName="resize-none"
          autoFocus
        />
        {error && (
          <p role="alert" className="rounded-lg border border-error/40 bg-error/[0.06] px-3 py-2 text-body-sm text-secondary">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
